import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Base64Tool from '../components/Base64Tool';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'What is MIME Base64?', a: 'MIME (RFC 2045) Base64 is standard Base64 wrapped at 76 characters per line, separated by CRLF line breaks. It is the format email clients use to encode attachments.' },
  { q: 'Why does MIME Base64 wrap at 76 characters?', a: 'Older email transport (SMTP) had line-length limits, so RFC 2045 requires encoded body lines to be no longer than 76 characters to guarantee safe delivery through any mail relay.' },
  { q: 'Is MIME Base64 different from regular Base64?', a: 'The underlying alphabet and encoding is identical — only the line wrapping differs. Decoding simply requires stripping the line breaks before running standard Base64 decoding.' },
  { q: 'Do I need MIME wrapping for APIs or JWTs?', a: 'No. APIs, JWTs and data URIs use unwrapped standard or URL-safe Base64. MIME wrapping is specific to email (and some legacy protocols) — see this tool\'s standard mode or the URL-Safe tool otherwise.' },
];

export default function Base64MimeEncodePage() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="MIME Base64 Encoder — RFC 2045 Line-Wrapped Base64 | EasyBase64"
        description="Encode text to MIME (RFC 2045) Base64, wrapped at 76 characters per line like email attachments. Decode line-wrapped Base64 back to text, free and private."
        path="/base64-mime-encode"
        jsonLd={[
          softwareApplicationSchema({ name: 'MIME Base64 Encoder', description: 'Encode and decode RFC 2045 line-wrapped MIME Base64 in the browser.', path: '/base64-mime-encode' }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'MIME Base64 Encode', path: '/base64-mime-encode' }]),
        ]}
      />
      <PageHero
        title="MIME Base64 Encoder"
        subtitle={<>Encode to RFC 2045 line-wrapped Base64, the format used for email attachments. Read more in <Link to="/base64-for-email-attachments" className="underline">Base64 for email attachments</Link>.</>}
      />
      <div className="mb-12">
        <Base64Tool initialMode="encode" variant="mime" />
      </div>
      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12 max-w-3xl mx-auto`}>
        <h2 className={`text-2xl font-bold mb-4 ${themeClasses.textPrimary}`}>Standard vs. MIME Base64</h2>
        <p className={`${themeClasses.textSecondary} leading-relaxed`}>
          Standard Base64 (used in APIs, data URIs and JWTs) is a single unbroken line. MIME Base64
          inserts a line break every 76 characters, a requirement inherited from early SMTP servers
          that could not reliably transmit arbitrarily long lines. Need unwrapped output? Use the{' '}
          <Link to="/base64-encode" className="underline">standard encoder</Link> instead.
        </p>
      </section>
      <FaqSection title="MIME Base64 — FAQ" faqs={FAQS} />
    </>
  );
}
